import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useKory } from "@/contexts/kory-context";
import { useSopRecorder } from "@/contexts/sop-recorder-context";
import { requestVersionReload } from "@/lib/chunk-reload";
import { hasReloadHold, useReloadHold } from "@/lib/reload-holds";
import { getPendingVersion } from "@/lib/version-nudge";

/**
 * Moves an open tab onto a newly deployed frontend at the next route change.
 *
 * A tab running an old build keeps talking to today's backend, and one value
 * the old code does not know can break a whole page. lib/version-nudge
 * notices the deploy; this reloads at a pathname change, where the URL already
 * points at the destination, so the user simply lands on the new build.
 * Query-string and hash changes do not count — they are in-page state.
 * Neither does a navigation that carries router state ("Edit in chat" and
 * similar handoffs): the destination page reads and clears that state on
 * mount, in the same commit as this effect, so a reload would lose it.
 *
 * It never reloads over work in flight: an SOP recording that has not saved,
 * a Kory turn that is starting or streaming, queued Kory messages, or a
 * reload hold (unsent composer text, a CoS session question still sending —
 * lib/reload-holds). A skipped reload waits for the next pathname change.
 * The recording, streaming and queue signals also register a reload hold, so
 * the reload buster's forced reload (lib/version-nudge) honors them too.
 */
export function ReloadOnNewVersion() {
  const { pathname, state: navState } = useLocation();
  const { unsaved } = useSopRecorder();
  const { isStreaming, isStartingTurn, queuedMessages } = useKory();
  const hasQueue = (queuedMessages?.length ?? 0) > 0;
  const lastPathname = useRef(pathname);
  useReloadHold(unsaved || isStreaming || hasQueue);

  useEffect(() => {
    if (pathname === lastPathname.current) return;
    lastPathname.current = pathname;
    const version = getPendingVersion();
    if (!version || navState != null) return;
    if (unsaved || isStreaming || hasQueue || isStartingTurn?.() || hasReloadHold()) return;
    requestVersionReload(version);
  }, [pathname, navState, unsaved, isStreaming, hasQueue, isStartingTurn]);

  return null;
}
